// DT-4a — **ONE MEMBER'S DELIVERY CHAIN, ON THE DOOR D6 ALREADY OPENS.**
//
// DT-4's own words: *"Her verification is NOT 'a message arrived' — it is the BEACON ROWS for her
// device showing received + displayed, the same evidence every member will produce."* `chainsForMember`
// has answered that question since DT-1 shipped and **nothing had ever asked it** — DT-1c's trap one
// read over, on the half that diagnoses a single phone rather than the whole morning.
//
// It lives in the member modal rather than on a surface of its own for one reason: **that payload's
// own privacy line is exactly the line this sits inside.** A tag, a kind and two timestamps are
// metadata; there is no field a notification's WORDS could ride in, which is the same property the
// counts beside it already have.
//
// Hookless, so the gate CALLS it — including in `never-sent` and `broken`, which are the two states a
// browser check would have to stage a broken morning to stand in front of.
import {
  memberDelivery, memberDeliveryNote, chainState,
} from "../deliveryVerdict.js";

// The shape `/api/owner/members/:id` answers with. Declared beside the one component that renders it,
// per this house's rule that a type moves with what draws it.
export type MemberDeliveryPayload = {
  rows: Array<{
    tag: string; kind: string; device: string;
    sent_at: string | null; received_at: string | null; displayed_at: string | null;
  }>;
  graceMinutes: number;
  now: number;
};

// `delivery: null` is a payload that has not arrived (R18 / W5-2's third state) and it holds still —
// which is not the same as a member nothing has ever been sent to, and that member gets a sentence.
export function MemberDeliverySection({ delivery }: { delivery: MemberDeliveryPayload | null }) {
  if (!delivery) return null;
  const summary = memberDelivery(delivery.rows, { graceMinutes: delivery.graceMinutes, now: delivery.now });
  const note = memberDeliveryNote(summary);
  return (
    <div className="owner-member__delivery">
      <h4>Notification delivery</h4>
      <div className="owner-health">
        <div className="owner-health__item"><strong>{summary.counts.delivered}</strong><span>Delivered</span></div>
        <div className="owner-health__item"><strong>{summary.counts.drewNothing}</strong><span>Never drew</span></div>
        <div className="owner-health__item"><strong>{summary.counts.silent}</strong><span>Never arrived</span></div>
        <div className="owner-health__item"><strong>{summary.counts.waiting}</strong><span>In grace</span></div>
      </div>

      {/* **THE ROWS ARE THE DELIVERABLE.** A member whose last twelve pushes drew and whose
          thirteenth vanished is not "92% fine" — she is one silent send, and a percentage is
          precisely what makes that invisible. Newest first, as the store serves them. */}
      {summary.rows.length ? (
        <ul className="owner-member__chain">
          {summary.rows.map((r) => (
            <li key={r.tag} className={`is-${r.state}`}>
              <strong>{chainState(r.state)?.label || r.state}</strong>
              <span>{r.kind || "push"}</span>
              {/* An unmeasurable age says so rather than printing a confident number (R18). */}
              <em>{r.minutes === null ? "age not recorded" : `${r.minutes.toLocaleString("en-US")} min ago`}</em>
            </li>
          ))}
        </ul>
      ) : null}

      {note ? <p className="owner-page__note" role="status">{note}</p> : null}
    </div>
  );
}
